import { useNavigate } from 'react-router-dom'

import { formatDateDDMMYYYY, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { Button } from '../../../components/Button/Button'

import { DocumentRecord } from '../../../models/Document'
import { DocumentBatch } from '../../../models/DocumentBatch'

import { clearHistoryMode, selectIsHistoryMode } from '../../../store/meetingSummary/contactUpdate'
import { displayDocumentSidebar } from '../../../store/processDocument/documentSidebar'
import { selectDocumentBatch, selectDocumentBatchContact } from '../../../store/processDocument/documentBatch'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { DocumentSidebarType, DocumentSidebarTitle } from '../DocumentSidebar/models'
import { getDocumentFile } from '../utils'

interface DocumentHeaderProps {
  document: DocumentRecord
}

export const DocumentHeader: React.FC<DocumentHeaderProps> = ({ document }) => {
  const dispatch = useAppDispatch()

  const navigate = useNavigate()

  const documentBatch = useAppSelector(selectDocumentBatch)
  const isHistoryMode = useAppSelector(selectIsHistoryMode)

  const handleBackToPackageButtonClicked = (documentBatchId: string) => {
    navigate(`/document-batches/${documentBatchId}`)
  }

  const handleBackButtonClicked = () => {
    navigate('/documents/upload')
  }

  const handleBackButtonClickedInHistoryMode = () => {
    dispatch(clearHistoryMode())
    dispatch(displayDocumentSidebar({ type: DocumentSidebarType.EXTERNAL_UPDATES, title: DocumentSidebarTitle.EXTERNAL_UPDATES, icon: 'bi-arrow-repeat' }))
  }

  return (
    <div className='d-flex zocks-pl-2.0 justify-content-between align-items-center'>
      {isHistoryMode ? (
        <Button variant='header' className='zocks-p-0.625' onClick={handleBackButtonClickedInHistoryMode}>
          <i className='bi bi-chevron-left' />
        </Button>
      ) : !documentBatch ? (
        <Button variant='header' className='zocks-p-0.625' onClick={handleBackButtonClicked}>
          <i className='bi bi-chevron-left' />
        </Button>
      ) : (
        <div className='d-flex align-items-center w-100'>
          <div className='d-flex align-items-center gap-2 flex-shrink-0 border-end pe-3 zocks-border-color-gray-300'>
            <Button variant='header' className='zocks-p-0.625' onClick={() => handleBackToPackageButtonClicked(documentBatch.id)}>
              <i className='bi bi-chevron-left' />
            </Button>
            <span className='paragraph-medium bold'>Back to package</span>
          </div>
          <DocumentBatchNavigation documentBatch={documentBatch} document={document} />
        </div>
      )}
    </div>
  )
}

interface DocumentBatchNavigationProps {
  documentBatch: DocumentBatch
  document: DocumentRecord
}

const DocumentBatchNavigation: React.FC<DocumentBatchNavigationProps> = ({ documentBatch, document }) => {
  const { documents, createdAt } = documentBatch
  const { id: documentId } = document

  const fileName = getDocumentFile(document)?.name

  const navigate = useNavigate()

  const contact = useAppSelector(selectDocumentBatchContact)

  const currentIndex = documents.findIndex((document) => document.id === documentId)

  const normalizedIndex = currentIndex > -1 ? currentIndex + 1 : currentIndex

  const navigateToNextDocument = (nextDocument: DocumentRecord) => {
    navigate(`/documents/${nextDocument.id}`)
  }

  if (!contact) {
    return null
  }

  return (
    <div className='px-3 flex-grow-1 d-flex align-items-center justify-content-between gap-3'>
      <span className='paragraph-medium bold text-truncate'>
        {formatDateDDMMYYYY(createdAt)} - {getUserDisplayName(contact)} / {fileName}
      </span>
      <div className='d-flex align-items-center gap-3'>
        <span className='paragraph-medium flex-shrink-0'>
          {normalizedIndex} of {documents.length}
        </span>
        <Button
          variant='borderless'
          className='zocks-p-0.625'
          disabled={!documents[currentIndex - 1]}
          onClick={() => navigateToNextDocument(documents[currentIndex - 1])}
        >
          <i className='bi bi-chevron-left' />
        </Button>
        <Button
          variant='borderless'
          className='zocks-p-0.625'
          disabled={!documents[currentIndex + 1]}
          onClick={() => navigateToNextDocument(documents[currentIndex + 1])}
        >
          <i className='bi bi-chevron-right' />
        </Button>
      </div>
    </div>
  )
}
